import { useEffect } from "react";
import { setPageMeta } from "../lib/pageMeta";

// Privacy Policy — full legal document (Round 7). The "Last updated" date
// comes from the document itself, never the clock. Every operational claim
// in this document was verified against the codebase before shipping (see
// the Round 7 verify-or-flag pass): deletion cascades cover content AND
// derived data in D1 + R2, BYOK keys are AES-256-GCM encrypted, and the
// provider list matches the deployed configuration.

const LAST_UPDATED = "June 10, 2026";

const SECTIONS: Array<[string, string]> = [
  ["what-we-collect", "1. What we collect"],
  ["processing", "2. How your content is processed"],
  ["where", "3. Where your data lives"],
  ["sharing", "4. Who we share data with"],
  ["retention", "5. Retention and deletion"],
  ["export", "6. Export"],
  ["security", "7. Security"],
  ["rights", "8. Your rights and choices"],
  ["cookies", "9. Cookies"],
  ["children", "10. Children"],
  ["changes", "11. Changes to this policy"],
  ["contact", "12. Contact"],
];

export default function PrivacyPage() {
  useEffect(() => {
    setPageMeta({
      title: "Privacy policy | CaseThread",
      description:
        "What CaseThread collects, how your case content is processed and by whom, where it lives, and the deletion and export controls you have.",
      canonical: "https://casethread.app/privacy",
    });
  }, []);

  return (
    <div className="legal-page">
      <header className="legal-header">
        <a href="/welcome" className="legal-brand">
          CaseThread
        </a>
        <nav>
          <a href="/terms">Terms</a>
        </nav>
      </header>
      <main className="legal-main">
        <h1>Privacy Policy</h1>
        <p className="muted">
          <strong>Last updated: {LAST_UPDATED}</strong>
        </p>

        <p>
          CaseThread is a personal correspondence manager. The data you
          forward, upload, paste, record, and enter is stored in your account,
          and we don't sell, share, or repurpose your content. This policy
          explains exactly what we collect, how it is processed, who processes
          it on our behalf, and the controls you have.
        </p>

        <nav className="legal-toc" aria-label="Table of contents">
          <ul>
            {SECTIONS.map(([id, title]) => (
              <li key={id}>
                <a href={`#${id}`}>{title}</a>
              </li>
            ))}
          </ul>
        </nav>

        <h2 id="what-we-collect">1. What we collect</h2>
        <p>
          <strong>Account information.</strong> Your name, email address, and
          login credentials, managed by our identity provider, Clerk. If you
          sign in with Google, we receive the basic profile information Google
          provides.
        </p>
        <p>
          <strong>Payment information.</strong> Handled entirely by Stripe. We
          never receive or store your card number. We keep records of your
          subscription status and billing history.
        </p>
        <p>
          <strong>Your case content.</strong> Everything you choose to put
          into CaseThread: forwarded emails (including content sent to your
          per-case @casethread.app addresses), uploaded files and attachments
          (documents, PDFs, images, audio recordings), pasted text, and notes.
          This often includes sensitive material — correspondence about
          disputes, financial figures, names of other people. We treat all of
          it as yours.
        </p>
        <p>
          <strong>Derived data.</strong> Facts, dates, amounts, quotes,
          summaries, and timelines that the Service extracts from your content
          in order to display your case to you.
        </p>
        <p>
          <strong>Usage and technical data.</strong> Basic logs needed to
          operate and secure the Service (such as request logs and error
          reports) and feature-usage counts (such as your AI-call usage
          against your plan's limit). We do not use third-party advertising
          trackers.
        </p>

        <h2 id="processing">2. How your content is processed</h2>
        <p>
          <strong>AI processing.</strong> To extract facts and build your case
          views, the text of your records is sent to third-party AI providers
          for processing. Depending on configuration, this may be Anthropic,
          OpenAI, or Google. We send what is needed for the feature to work —
          the content of the record being processed — and use the results
          only to provide the Service to you. We do not use your content to
          train AI models, and we use provider API tiers that, per the
          providers' terms, do not use API-submitted content to train their
          models.
        </p>
        <p>
          <strong>Bring-your-own-key (BYOK).</strong> If you configure your
          own AI provider API key, your content is processed by that provider
          under your own agreement with them, using your key. Your keys are
          stored encrypted and used only to make the requests you initiate.
        </p>
        <p>
          <strong>Audio and images.</strong> Audio recordings are transcribed
          and treated as lower-confidence sources; images may be processed to
          produce short descriptive labels. The same AI-provider processing
          described above applies.
        </p>
        <p>
          <strong>No human review.</strong> We do not read your case content
          in the ordinary course of operating the Service. Limited access may
          occur only if necessary to debug a problem you report, to
          investigate abuse, or where required by law — and only to the
          minimum extent needed.
        </p>

        <h2 id="where">3. Where your data lives</h2>
        <p>
          Your content and derived data are stored on Cloudflare
          infrastructure (D1 databases, R2 object storage, and Workers), in
          encrypted form at rest and in transit. Authentication data is held
          by Clerk; payment data by Stripe.
        </p>

        <h2 id="sharing">4. Who we share data with</h2>
        <p>
          We share data only with the service providers needed to run
          CaseThread, and only for that purpose:
        </p>
        <ul>
          <li>
            <strong>Cloudflare</strong> — hosting, storage, and application
            infrastructure
          </li>
          <li>
            <strong>Clerk</strong> — account authentication
          </li>
          <li>
            <strong>Stripe</strong> — payment processing
          </li>
          <li>
            <strong>Anthropic, OpenAI, and/or Google</strong> — AI processing
            of your content as described above
          </li>
        </ul>
        <p>
          We do not sell your personal information, and we do not share it
          with advertisers or data brokers. We may disclose information if
          required by law (for example, a valid subpoena), and we will notify
          you of such requests where the law allows.
        </p>
        <p>
          If you use the Share or export features, you control what is shared
          and with whom — anything you export or share leaves the Service
          under your direction.
        </p>

        <h2 id="retention">5. Retention and deletion</h2>
        <ul>
          <li>
            <strong>While your account is active</strong>, your content is
            retained so the Service can work.
          </li>
          <li>
            <strong>Deleting content.</strong> When you delete a record, case,
            or other content, it is removed from your active data, and derived
            data based on it is removed or queued for removal; residual copies
            in backups expire on a rolling basis within approximately 30 days.
          </li>
          <li>
            <strong>Deleting your account.</strong> You can delete your
            account at any time. This permanently removes your content and
            derived data on the same schedule. Billing records are retained as
            required for tax and accounting purposes.
          </li>
          <li>
            <strong>Deletion is permanent.</strong> We cannot recover deleted
            content for you. Because material in CaseThread may matter to a
            dispute, export anything you need <strong>before</strong> deleting
            it, and always keep your original documents outside the Service.
          </li>
        </ul>

        <h2 id="export">6. Export</h2>
        <p>
          You can export your data at any time — including case dossiers,
          timelines, and your underlying files — using the export features in
          the app. We believe your case file should never be locked in.
        </p>

        <h2 id="security">7. Security</h2>
        <p>
          We use industry-standard measures appropriate to a service of our
          size: encryption in transit and at rest, authenticated access to all
          case data, isolation of each account's data, and deployment checks
          that verify access controls. No system is perfectly secure; if we
          learn of a breach affecting your personal information, we will
          notify you as required by law.
        </p>

        <h2 id="rights">8. Your rights and choices</h2>
        <p>
          Depending on where you live (including under the California Consumer
          Privacy Act and similar state laws), you may have rights to access,
          correct, delete, or obtain a copy of your personal information, and
          to ask what we collect and how it is used. CaseThread is built so
          you can exercise most of these directly: your data is visible in the
          app, exportable at any time, and deletable by you. For anything you
          cannot do in the app, contact us and we will respond as the law
          requires. We do not sell personal information, so there is nothing
          to opt out of selling. We do not discriminate against you for
          exercising your rights.
        </p>

        <h2 id="cookies">9. Cookies</h2>
        <p>
          We use only the cookies necessary for the Service to function —
          primarily session cookies from our authentication provider so you
          stay signed in. We do not use advertising cookies.
        </p>

        <h2 id="children">10. Children</h2>
        <p>
          CaseThread is not directed to children and may not be used by
          anyone under 18. We do not knowingly collect information from
          children; if we learn we have, we will delete it.
        </p>

        <h2 id="changes">11. Changes to this policy</h2>
        <p>
          We may update this policy from time to time. For material changes,
          we will provide notice (for example, by email or an in-app notice)
          before the changes take effect. The "Last updated" date above
          reflects the current version.
        </p>

        <h2 id="contact">12. Contact</h2>
        <p>
          Privacy questions or requests: use the Feedback link in the app or
          the contact information on casethread.app.
        </p>
      </main>
      <footer className="legal-footer">
        <p>
          <a href="/guides">Read the dispute resolution guides</a> ·{" "}
          <a href="/welcome">About CaseThread</a>
        </p>
        <p className="muted">© 2026 CaseThread</p>
      </footer>
    </div>
  );
}
